<script lang="ts" setup>
	/**
	 * Pile de messages transitoires (toasts).
	 *
	 * Se place une seule fois à la racine de l'application. Les erreurs d'API non
	 * silencieuses y arrivent automatiquement via l'observateur du `Caller` : les
	 * vues n'ont pas à les intercepter pour qu'elles soient visibles.
	 */
	import { useToastStore, type ToastVariant } from '@common/stores/ToastStore';

	withDefaults(
		defineProps<{
			location?: string;
			/** Cibles tactiles et texte agrandis pour la borne. */
			large?: boolean;
		}>(),
		{
			location: 'top right',
			large: false,
		},
	);

	const toasts = useToastStore();

	const ICONS: Record<ToastVariant, string> = {
		success: 'mdi-check-circle-outline',
		info: 'mdi-information-outline',
		warning: 'mdi-alert-outline',
		error: 'mdi-alert-circle-outline',
	};
</script>

<template>
	<div class="components-notify">
		<v-snackbar
			v-for="(toast, index) of toasts.toasts"
			:key="toast.id"
			:model-value="true"
			:color="toast.variant"
			:timeout="-1"
			:location="location as any"
			:class="{ 'components-notify--large': large }"
			:style="{ marginTop: `${index * (large ? 96 : 72)}px` }"
			@update:model-value="toasts.dismiss(toast.id)"
		>
			<div class="d-flex align-center ga-3">
				<v-icon :size="large ? 32 : 24">{{ toast.icon ?? ICONS[toast.variant] }}</v-icon>
				<div>
					<div v-if="toast.title" class="font-weight-bold">{{ toast.title }}</div>
					<div v-if="toast.message">{{ toast.message }}</div>
				</div>
			</div>

			<template #actions>
				<v-btn v-if="toast.action" variant="text" @click="toast.action.handler()">
					{{ toast.action.label }}
				</v-btn>
				<v-btn variant="text" icon="mdi-close" size="small" @click="toasts.dismiss(toast.id)" />
			</template>
		</v-snackbar>
	</div>
</template>

<style lang="scss">
	.components-notify--large {
		.v-snackbar__wrapper {
			min-height: 72px;
			font-size: 1.15rem;
		}
	}
</style>
